import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import InitialsAvatar from '../components/InitialsAvatar';
import StatCard from '../components/StatCard';
import ComingSoon from '../components/ComingSoon';
import PlayerProfileActions from '../components/PlayerProfileActions';

import { generatePlayerRecommendation } from '../utils/insights';
import { formatAppearances, formatPlayerGPG, hasKnownAppearances } from '../utils/formatters';

const sectionKicker = 'font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant';

export default function PlayerProfile() {
  const { id } = useParams();
  let playerName;
  try {
    playerName = decodeURIComponent(id);
  } catch {
    playerName = id;
  }
  const navigate = useNavigate();

  const { data: searchData, loading } = useApi(`/api/search?term=${encodeURIComponent(playerName)}&type=players`);
  const { data: allPlayersData } = useApi('/api/top-scorers?limit=500');

  const player = searchData?.results?.find((r) => r.player_name === playerName || r.name === playerName);
  const { data: profileData } = useApi(player?.player_id ? `/api/player-profile/${player.player_id}` : null, [player?.player_id]);
  const profile = profileData?.profile || null;
  const allPlayers = allPlayersData?.stats || [];

  const recommendation = player ? generatePlayerRecommendation(
    { ...player, player_name: player.player_name || player.name },
    allPlayers
  ) : null;

  const leaguePlayers = allPlayers.filter((p) => p.league_name === player?.league_name);
  const leagueAvgGPG = leaguePlayers.length > 0
    ? leaguePlayers.reduce((sum, p) => sum + (p.goals_per_game || 0), 0) / leaguePlayers.length
    : 0;
  const leagueAvgGoals = leaguePlayers.length > 0
    ? leaguePlayers.reduce((sum, p) => sum + (p.total_goals || 0), 0) / leaguePlayers.length
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">progress_activity</span>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">person_off</span>
        <p className={`${sectionKicker} mb-1`}>Player Profile</p>
        <h2 className="font-expanded font-headline text-2xl sm:text-3xl font-bold text-on-surface tracking-tight mb-2">Player Not Found</h2>
        <p className="text-sm text-on-surface-variant mb-6">"{playerName}" could not be found.</p>
        <button
          onClick={() => navigate('/players')}
          className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/30 bg-surface-container px-4 py-2.5 text-sm font-bold text-primary hover:border-primary/50 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back to Players
        </button>
      </div>
    );
  }

  const goals = player.total_goals || 0;
  const apps = player.total_appearances || 0;
  const gpg = player.goals_per_game || (apps > 0 ? goals / apps : 0);
  const knownApps = hasKnownAppearances(player);
  const name = profile?.display_name || player.player_name || player.name;
  const club = player.club_name;
  const clubUrl = player.club_website || null;
  const heroPosition = profile?.primary_position || player.position || 'ST';
  const ageDisplay = profile?.year_of_birth ? new Date().getFullYear() - Number(profile.year_of_birth) : '—';
  const footDisplay = profile?.preferred_foot
    ? profile.preferred_foot.charAt(0).toUpperCase() + profile.preferred_foot.slice(1)
    : '—';
  const nationalityDisplay = profile?.nationality || player.nationality || '—';

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="relative rounded-xl overflow-hidden bg-surface-container-low border border-outline-variant/30">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-surface-container-low to-secondary/5" />
        <div className="relative z-10 p-5 sm:p-8 w-full flex flex-col md:flex-row md:items-end justify-between gap-6 md:gap-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            <div className="relative self-start">
              {profile?.profile_photo_url ? (
                <img
                  src={profile.profile_photo_url}
                  alt={name}
                  className="w-32 h-32 rounded-2xl object-cover border border-outline-variant/30"
                />
              ) : (
                <InitialsAvatar name={name} clubName={club} size="xl" />
              )}
              <div className="absolute -bottom-2 -right-2 bg-secondary px-2 py-1 text-[11px] font-black text-on-secondary-fixed rounded-md">
                {heroPosition}
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-label text-xs font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
                {clubUrl ? (
                  <a href={clubUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline underline-offset-2">
                    {player.club_name}
                  </a>
                ) : (
                  <span className="text-on-surface">{player.club_name}</span>
                )}
                {' '}&bull; {player.league_name}
              </p>
              <h2 className="font-expanded font-headline text-4xl sm:text-5xl font-extrabold text-on-surface tracking-tight">{name}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-x-6 text-on-surface-variant font-medium pt-1">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant/80">AGE</span>
                  <span className="text-on-surface text-sm tabular-nums">{ageDisplay}</span>
                </div>
                <div className="w-px h-8 bg-outline-variant/40" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant/80">FOOT</span>
                  <span className="text-on-surface text-sm">{footDisplay}</span>
                </div>
                <div className="w-px h-8 bg-outline-variant/40" />
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-on-surface-variant/80">NATIONALITY</span>
                  <span className="text-on-surface text-sm">{nationalityDisplay}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${
              profile?.profile_status === 'claimed'
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-outline-variant/30 bg-surface-container-high text-on-surface-variant'
            }`}>
              <span className="material-symbols-outlined text-[16px]">
                {profile?.profile_status === 'claimed' ? 'verified' : 'shield_person'}
              </span>
              {profile?.profile_status === 'claimed' ? 'Player Claimed' : 'Unclaimed'}
            </div>
            <button className="rounded-xl border border-outline-variant/30 bg-surface-container-highest text-on-surface-variant font-bold py-2.5 px-5 text-sm cursor-default opacity-60">
              Export Scouting Report
            </button>
          </div>
        </div>
      </section>

      {/* Stats + Sidebar */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard label="Total Goals" value={goals} accentColor="text-secondary" icon="sports_soccer" />
            <StatCard label="Appearances" value={formatAppearances(player)} accentColor="text-on-surface" />
            <StatCard label="GPG Ratio" value={formatPlayerGPG(player)} accentColor="text-primary" subtitle={knownApps && gpg >= 1.0 ? 'Elite Percentile' : null} />
            <StatCard label="League Rank" value={recommendation ? `#${recommendation.rank}` : '—'} accentColor="text-on-surface" subtitle={`of ${allPlayers.length} players`} />
          </div>

          {profile?.bio && (
            <div className="bg-surface-container p-5 sm:p-6 rounded-xl border border-outline-variant/30">
              <p className={sectionKicker}>Profile</p>
              <h4 className="font-headline text-lg font-bold text-on-surface mt-1 mb-3">About</h4>
              <p className="text-sm sm:text-[15px] leading-relaxed text-on-surface-variant max-w-[65ch]">{profile.bio}</p>
            </div>
          )}

          {/* Career History */}
          <div className="bg-surface-container p-5 sm:p-6 rounded-xl border border-outline-variant/30">
            <p className={sectionKicker}>Career</p>
            <h4 className="font-headline text-lg font-bold text-on-surface mt-1 mb-5">History</h4>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-left">
                <thead>
                  <tr className="font-label text-[10px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] border-b border-outline-variant/30">
                    <th className="pb-3 pl-2">Season</th>
                    <th className="pb-3">Club</th>
                    <th className="pb-3">League</th>
                    <th className="pb-3 text-center">Apps</th>
                    <th className="pb-3 text-center">Goals</th>
                  </tr>
                </thead>
                <tbody className="text-sm font-medium">
                  <tr className="group hover:bg-surface-container-high transition-colors duration-150">
                    <td className="py-3 border-l-2 border-transparent group-hover:border-primary pl-2 text-on-surface-variant tabular-nums">{player.season || '2025/26'}</td>
                    <td className="py-3 text-on-surface font-semibold">
                      {clubUrl ? (
                        <a href={clubUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors duration-150 underline decoration-outline-variant/30 underline-offset-2 hover:decoration-primary">
                          {player.club_name}
                        </a>
                      ) : player.club_name}
                    </td>
                    <td className="py-3 text-on-surface-variant">{player.league_name}</td>
                    <td className="py-3 text-center text-on-surface-variant tabular-nums">{formatAppearances(player)}</td>
                    <td className="py-3 text-center font-headline font-bold text-secondary tabular-nums">{goals}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <PlayerProfileActions player={player} profile={profile} />

          {(profile?.x_url || profile?.instagram_url || profile?.highlight_url || profile?.hudl_url || profile?.veo_url) && (
            <div className="bg-surface-container p-5 sm:p-6 rounded-xl border border-outline-variant/30">
              <p className={sectionKicker}>Elsewhere</p>
              <h4 className="font-headline text-lg font-bold text-on-surface mt-1 mb-4">Links</h4>
              <div className="space-y-2">
                {[
                  ['X', profile.x_url],
                  ['Instagram', profile.instagram_url],
                  ['Highlights', profile.highlight_url],
                  ['Hudl', profile.hudl_url],
                  ['Veo', profile.veo_url],
                ].filter(([, url]) => url).map(([label, url]) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-lg bg-surface-container-high border border-outline-variant/20 px-3 py-2.5 text-sm font-semibold text-on-surface-variant hover:text-primary hover:border-primary/40 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                  >
                    <span>{label}</span>
                    <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Comparative Stats */}
          <div className="bg-surface-container p-5 sm:p-6 rounded-xl border border-outline-variant/30">
            <p className={sectionKicker}>Vs League Average</p>
            <h4 className="font-headline text-lg font-bold text-on-surface mt-1 mb-6">Comparative Stats</h4>
            <div className="space-y-5">
              <div className="space-y-1.5">
                <div className="flex justify-between items-baseline text-[11px] font-semibold uppercase tracking-[0.12em]">
                  <span className="text-on-surface-variant">Goals vs League Avg</span>
                  <span className="font-headline font-bold text-secondary tabular-nums text-sm tracking-normal">{goals} / {leagueAvgGoals.toFixed(0)}</span>
                </div>
                <div className="h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-secondary" style={{ width: `${Math.min((goals / Math.max(leagueAvgGoals * 2, 1)) * 100, 100)}%` }} />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-baseline text-[11px] font-semibold uppercase tracking-[0.12em]">
                  <span className="text-on-surface-variant">GPG vs League Avg</span>
                  <span className="font-headline font-bold text-primary tabular-nums text-sm tracking-normal">{knownApps ? gpg.toFixed(2) : '—'} / {leagueAvgGPG.toFixed(2)}</span>
                </div>
                <div className="h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min((gpg / Math.max(leagueAvgGPG * 2, 0.01)) * 100, 100)}%` }} />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-baseline text-[11px] font-semibold uppercase tracking-[0.12em]">
                  <span className="text-on-surface-variant">Apps vs League Avg</span>
                  <span className="font-headline font-bold text-on-surface tabular-nums text-sm tracking-normal">{formatAppearances(player)} / {leaguePlayers.length > 0 ? (leaguePlayers.reduce((s, p) => s + (p.total_appearances || 0), 0) / leaguePlayers.length).toFixed(0) : 0}</span>
                </div>
                <div className="h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-on-surface-variant" style={{ width: `${Math.min((apps / Math.max((leaguePlayers.reduce((s, p) => s + (p.total_appearances || 0), 0) / Math.max(leaguePlayers.length, 1)) * 2, 1)) * 100, 100)}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Scout Recommendation */}
          {recommendation && (
            <div className="bg-surface-container-high p-5 sm:p-6 rounded-xl border border-outline-variant/30 border-l-4 border-l-secondary relative overflow-hidden">
              <div className="absolute -right-2 -bottom-2 text-secondary/5 rotate-12">
                <span className="material-symbols-outlined text-[120px]">edit_note</span>
              </div>
              <div className="flex items-center gap-2 mb-4 text-secondary">
                <span className="material-symbols-outlined text-[20px]">verified</span>
                <h4 className="font-label text-[11px] font-semibold uppercase tracking-[0.12em]">Scout's Analysis</h4>
              </div>
              <p className="relative text-on-surface leading-relaxed text-sm">
                {recommendation.text}
              </p>
            </div>
          )}

          {/* Heatmap Placeholder */}
          <ComingSoon label="Positional Heatmap" />
        </div>
      </div>
    </div>
  );
}
